// Use Vite-style env variable; fallback to same-origin /api if not provided
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

// Import token utilities
import {
  getTokenFromCookie,
  isTokenExpired,
  isTokenExpiringSoon,
  refreshAccessToken,
  clearTokenCookies,
} from "./services/authService";

async function handleResponse(response) {
  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let errorMessage = `Request failed with ${response.status}`;
    let errorCode = null;
    let errorData = null;

    if (contentType && contentType.includes("application/json")) {
      try {
        errorData = await response.json();
        if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstDetail = errorData.detail[0];
          if (typeof firstDetail === "string") {
            errorMessage = firstDetail;
          } else if (firstDetail && firstDetail.msg) {
            const fieldPath = Array.isArray(firstDetail.loc)
              ? firstDetail.loc.filter((part) => part !== "body").join(".")
              : "";
            errorMessage = fieldPath
              ? `${fieldPath}: ${firstDetail.msg}`
              : firstDetail.msg;
          }
        } else if (errorData.detail && errorData.detail.message) {
          errorMessage = errorData.detail.message;
          errorCode = errorData.detail.code || null;
        } else if (errorData.message) {
          errorMessage = errorData.message;
          errorCode = errorData.code || null;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail && typeof errorData.detail === "string") {
          errorMessage = errorData.detail;
        }
      } catch (e) {
        errorMessage = await response.text();
      }
    } else {
      errorMessage = await response.text();
    }

    const err = new Error(errorMessage);
    if (errorCode) err.code = errorCode;
    err.status = response.status;
    err.payload = errorData;
    throw err;
  }

  // ── Handle empty responses (204 No Content, or no body) ──
  const contentLength = response.headers.get("content-length");
  if (
    response.status === 204 ||
    contentLength === "0" ||
    (contentLength === null && response.status >= 200 && response.status < 300)
  ) {
    const text = await response.text();
    if (!text) return true; // empty body — success
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return response.text();
}
export async function apiRequest(path, options = {}) {
  const { headers, body, ...rest } = options;

  // Skip token refresh for auth endpoints
  const isAuthEndpoint =
    path.includes("/auth/login") ||
    path.includes("/auth/register") ||
    path.includes("/auth/refresh");

  // Check if token needs refresh (unless it's an auth endpoint)
  if (!isAuthEndpoint) {
    const accessToken = getTokenFromCookie("access_token");

    // Refresh if token is expired or expiring soon
    if (accessToken && isTokenExpiringSoon(accessToken)) {
      try {
        // Try to refresh the token
        await refreshAccessToken();
      } catch (error) {
        // Refresh failed, redirect to login
        clearTokenCookies();
        window.location.href = "/login";
        throw new Error("Session expired. Please login again.");
      }
    }
  }

  // Get the (possibly refreshed) access token
  const accessToken = getTokenFromCookie("access_token");

  const requestInit = {
    headers: {
      "Content-Type": "application/json",
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    ...rest,
  };

  const response = await fetch(`${API_BASE_URL}${path}`, requestInit);
  return handleResponse(response);
}

export const apiClient = {
  get: (path, options) => apiRequest(path, { method: "GET", ...options }),
  post: (path, body, options) =>
    apiRequest(path, { method: "POST", body, ...options }),
  put: (path, body, options) =>
    apiRequest(path, { method: "PUT", body, ...options }),
  del: (path, options) => apiRequest(path, { method: "DELETE", ...options }),
};

/**
 * Send a multipart/form-data request (e.g. file uploads).
 * Handles token refresh the same way as apiRequest.
 */
export async function apiFormDataRequest(path, formData) {
  const isAuthEndpoint = false;

  if (!isAuthEndpoint) {
    const accessToken = getTokenFromCookie("access_token");
    if (accessToken && isTokenExpiringSoon(accessToken)) {
      try {
        await refreshAccessToken();
      } catch (error) {
        clearTokenCookies();
        window.location.href = "/login";
        throw new Error("Session expired. Please login again.");
      }
    }
  }

  const accessToken = getTokenFromCookie("access_token");

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      // Do NOT set Content-Type – browser sets it with boundary for FormData
    },
    body: formData,
  });

  return handleResponse(response);
}
